/**
 * MusicRoute — search, download, and trim music.
 *
 * Accessible from the chat header nav (🎵). Shares the same glass
 * aesthetic and theme background as the chat surface.
 */

import { useCallback, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import {
  downloadTrack,
  formatDuration,
  searchMusic,
  trimUploadedTrack,
  type MusicTrack,
  type TrimOptions,
} from '../infra/musicClient';

import styles from './MusicRoute.module.css';

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'results'; tracks: MusicTrack[]; query: string }
  | { kind: 'error'; code: string };

const DEFAULT_TRIM_START = 0;
const DEFAULT_TRIM_DURATION = 30;
const MAX_TRIM_START = 7200;
const MAX_TRIM_DURATION = 600;

function clampSeconds(value: number, fallback: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

export function MusicRoute(): ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [state, setState] = useState<SearchState>({ kind: 'idle' });
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [trimDownloads, setTrimDownloads] = useState(false);
  const [trimStart, setTrimStart] = useState(DEFAULT_TRIM_START);
  const [trimDuration, setTrimDuration] = useState(DEFAULT_TRIM_DURATION);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const getTrimOptions = useCallback((): TrimOptions => ({
    start: clampSeconds(trimStart, DEFAULT_TRIM_START, 0, MAX_TRIM_START),
    duration: clampSeconds(trimDuration, DEFAULT_TRIM_DURATION, 1, MAX_TRIM_DURATION),
  }), [trimDuration, trimStart]);

  const runSearch = useCallback(async (raw: string): Promise<void> => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return;

    setState({ kind: 'loading' });
    try {
      const tracks = await searchMusic(trimmed);
      setState({ kind: 'results', tracks, query: trimmed });
    } catch (err) {
      const code = err instanceof Error ? err.message : 'search_failed';
      setState({ kind: 'error', code });
    }
  }, []);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    void runSearch(query);
  };

  const handleDownload = async (track: MusicTrack): Promise<void> => {
    if (downloadingId !== null) return;
    setDownloadingId(track.id);
    try {
      await downloadTrack(track, trimDownloads ? getTrimOptions() : undefined, () => {});
    } catch {
      setState({ kind: 'error', code: 'download_failed' });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleUploadChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUploadFile(event.target.files?.[0] ?? null);
  };

  const handleUploadTrim = async (): Promise<void> => {
    if (uploadFile === null || uploading) return;
    setUploading(true);
    try {
      await trimUploadedTrack(uploadFile, getTrimOptions(), () => {});
    } catch {
      setState({ kind: 'error', code: 'upload_trim_failed' });
    } finally {
      setUploading(false);
    }
  };

  const errorMessage = (code: string): string => {
    switch (code) {
      case 'rate_limit':
        return t('music.errors.rateLimit');
      case 'unauthorized':
        return t('music.errors.unauthorized');
      case 'download_failed':
        return t('music.errors.downloadFailed');
      case 'upload_trim_failed':
        return t('music.errors.uploadTrimFailed');
      default:
        return t('music.errors.searchFailed');
    }
  };

  return (
    <div className={styles.page} data-testid="music-route">
      <header className={styles.header}>
        <Link to="/" className={styles.backBtn} aria-label={t('music.backToChat')}>
          ←
        </Link>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{t('music.title')}</h1>
          <p className={styles.subtitle}>{t('music.subtitle')}</p>
        </div>
      </header>

      <main className={styles.main}>
        <motion.section
          className={styles.searchCard}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <div className={styles.searchIcon} aria-hidden="true">🎵</div>
          <form className={styles.searchForm} onSubmit={handleSubmit}>
            <label htmlFor="music-search" className={styles.searchLabel}>
              {t('music.searchLabel')}
            </label>
            <div className={styles.searchRow}>
              <input
                ref={inputRef}
                id="music-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('music.searchPlaceholder')}
                className={styles.searchInput}
                autoComplete="off"
                enterKeyHint="search"
                maxLength={200}
              />
              <button
                type="submit"
                className={styles.searchBtn}
                disabled={state.kind === 'loading' || query.trim().length === 0}
              >
                {state.kind === 'loading' ? t('music.searching') : t('music.search')}
              </button>
            </div>
          </form>
          <p className={styles.hint}>{t('music.hint')}</p>

          <div className={styles.trimPanel}>
            <div className={styles.trimHeader}>
              <span className={styles.trimTitle}>{t('music.trim.title')}</span>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={trimDownloads}
                  onChange={(e) => setTrimDownloads(e.target.checked)}
                  className={styles.toggleInput}
                />
                <span>{t('music.trim.downloadToggle')}</span>
              </label>
            </div>
            <div className={styles.numberGrid}>
              <label className={styles.numberField}>
                <span>{t('music.trim.start')}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={MAX_TRIM_START}
                  step={1}
                  value={trimStart}
                  onChange={(e) => setTrimStart(Number(e.target.value))}
                  className={styles.numberInput}
                />
              </label>
              <label className={styles.numberField}>
                <span>{t('music.trim.duration')}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MAX_TRIM_DURATION}
                  step={1}
                  value={trimDuration}
                  onChange={(e) => setTrimDuration(Number(e.target.value))}
                  className={styles.numberInput}
                />
              </label>
            </div>
            <p className={styles.trimHint}>{t('music.trim.hint')}</p>
          </div>
        </motion.section>

        <motion.section
          className={styles.uploadPanel}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.04, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <div className={styles.uploadText}>
            <h2 className={styles.uploadTitle}>{t('music.upload.title')}</h2>
            <p className={styles.uploadHint}>{t('music.upload.hint')}</p>
          </div>
          <div className={styles.uploadControls}>
            <label className={styles.filePicker}>
              <span>{t('music.upload.choose')}</span>
              <input
                type="file"
                accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac"
                onChange={handleUploadChange}
                className={styles.fileInput}
              />
            </label>
            <button
              type="button"
              className={styles.uploadBtn}
              onClick={() => { void handleUploadTrim(); }}
              disabled={uploadFile === null || uploading}
              aria-busy={uploading}
            >
              {uploading ? t('music.upload.processing') : t('music.upload.action')}
            </button>
          </div>
          {uploadFile !== null ? (
            <p className={styles.selectedFile}>{uploadFile.name}</p>
          ) : null}
        </motion.section>

        <AnimatePresence mode="wait">
          {state.kind === 'error' ? (
            <motion.div
              key="error"
              className={styles.errorBanner}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              role="alert"
            >
              {errorMessage(state.code)}
            </motion.div>
          ) : null}

          {state.kind === 'loading' ? (
            <motion.div
              key="loading"
              className={styles.loadingBlock}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <span className={styles.spinner} aria-hidden="true" />
              <p>{t('music.searching')}</p>
            </motion.div>
          ) : null}

          {state.kind === 'results' ? (
            <motion.section
              key="results"
              className={styles.results}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              aria-live="polite"
            >
              <h2 className={styles.resultsHeading}>
                {state.tracks.length > 0
                  ? t('music.resultsFor', { query: state.query, count: state.tracks.length })
                  : t('music.noResults')}
              </h2>

              {state.tracks.length === 0 ? (
                <p className={styles.emptyHint}>{t('music.noResultsHint')}</p>
              ) : (
                <ul className={styles.trackList}>
                  {state.tracks.map((track, index) => (
                    <motion.li
                      key={track.id}
                      className={styles.trackItem}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04, duration: 0.25 }}
                    >
                      <div className={styles.thumbWrap}>
                        {track.thumbnail ? (
                          <img
                            src={track.thumbnail}
                            alt=""
                            className={styles.thumb}
                            loading="lazy"
                          />
                        ) : (
                          <div className={styles.thumbFallback} aria-hidden="true">♪</div>
                        )}
                      </div>
                      <div className={styles.trackMeta}>
                        <span className={styles.trackTitle}>{track.title}</span>
                        <span className={styles.trackChannel}>
                          {track.channel || '—'}
                          {' · '}
                          {formatDuration(track.duration)}
                        </span>
                      </div>
                      <button
                        type="button"
                        className={styles.downloadBtn}
                        onClick={() => { void handleDownload(track); }}
                        disabled={downloadingId !== null}
                        aria-busy={downloadingId === track.id}
                      >
                        {downloadingId === track.id
                          ? t('music.downloading')
                          : trimDownloads ? t('music.downloadClip') : t('music.download')}
                      </button>
                    </motion.li>
                  ))}
                </ul>
              )}
            </motion.section>
          ) : null}

          {state.kind === 'idle' ? (
            <motion.div
              key="idle"
              className={styles.idleBlock}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <p className={styles.idleText}>{t('music.idleHint')}</p>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </main>
    </div>
  );
}
